/**
 * Analytics Helper Functions
 * Contains utility functions specific to analytics KPI widgets and data visualization
 */

/**
 * Check if a KPI widget has a zero value
 * @param widget - Widget object containing price field
 * @returns true if the numeric value is zero, false otherwise
 */
export const isKPIValueZero = (widget: any): boolean => {
  const numericValue = parseFloat(widget.price.replace(/[^0-9.-]/g, '')) || 0;
  return numericValue === 0;
};

/**
 * Get customized zero-value message for each KPI type
 * @param title - KPI title (e.g., "Est. Spend", "AVG. CTR")
 * @returns Object containing heading and description for zero-value state
 */
export const getZeroValueMessage = (title: string): { heading: string; description: string } => {
  const messages: { [key: string]: { heading: string; description: string } } = {
    // Widgets.tsx KPIs (Slide 1 - Simple Aggregations)
    'Est. Spend': {
      heading: 'No Spend Recorded',
      description: 'No advertising spend has been recorded for the selected period.'
    },
    'Impressions': {
      heading: 'No Impressions Served',
      description: 'No ad impressions were served during the selected period.'
    },
    'Reach': {
      heading: 'No Reach Data Available',
      description: 'Unique reach data is not available for this campaign or period.'
    },
    'Clicks': {
      heading: 'No Clicks Recorded',
      description: 'No clicks were recorded for the selected period.'
    },
    // WidgetsSecondSlide.tsx KPIs (Slide 2 - Calculated Metrics)
    'AVG. CTR': {
      heading: 'No Click-Through Rate',
      description: 'No clicks were recorded relative to impressions served.'
    },
    'AVG. CPC': {
      heading: 'No Cost Per Click',
      description: 'Either no clicks or no spend was recorded for the selected period.'
    },
    'AVG. CVR': {
      heading: 'No Conversion Rate',
      description: 'No conversions were recorded relative to clicks received.'
    },
    'AVG. Conv. Rate': {
      heading: 'No Conversion Rate',
      description: 'No conversions were recorded relative to clicks received.'
    },
    'AVG. CPA': {
      heading: 'No Cost Per Acquisition',
      description: 'Either no conversions or no spend was recorded for the selected period.'
    }
  };

  // Return specific message or generic fallback
  return messages[title] || {
    heading: `No ${title} Recorded`,
    description: 'This metric has a value of zero for the selected period.'
  };
};
